
import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";

import {
    CreatableSelectRow,
    InputRow,
    SelectRow,
    DateRow,
    DetailRow,
} from "../Rows";
import { FormCard, FormGrid } from "../FormCard";

const EvseSettingsForm = ({
    chargingPoint = {},
    chargingConnector = {},
    handleInputChange,
    invalidFields = [],
    parkingSpaces = [],
}) => {
    const { t } = useTranslation();
    const src = Object.keys(chargingConnector || {}).length
        ? chargingConnector
        : chargingPoint || {};

    const connector = {
        connectors: Array.isArray(src.chargingConnectors)
            ? src.chargingConnectors
            : src.chargingConnectorPublicId
                ? [
                    {
                        chargingConnectorPublicId: src.chargingConnectorPublicId,
                        chargingConnectorNumber: src.chargingConnectorNumber,
                        chargingConnectorName: src.chargingConnectorName,
                        standard: src.standard,
                    },
                ]
                : [],
        evseCapabilities: src.evseCapabilities ?? src.capabilities ?? [],
        directions: src.directions ?? [],
        parkingRestrictions: src.parkingRestrictions ?? [],
        parkings: src.parkings ?? [],
        statusSchedules: src.statusSchedules ?? [],
        floorLevel: src.floorLevel ?? "",
        latitude: src.latitude ?? "",
        longitude: src.longitude ?? "",
        physicalReference: src.physicalReference ?? "",
        acceptedServiceProviders: src.acceptedServiceProviders ?? [],
    };

    const isInvalid = (k) => invalidFields?.includes?.(k);
    const onChange = (key, value) => handleInputChange?.(key, value);

    const connectorOptions = (connector.connectors || []).map((c) => ({
        value:
            c.chargingConnectorPublicId ||
            `${c.chargingConnectorNumber}-${c.standard}`,
        label: `#${c.chargingConnectorNumber} • ${c.chargingConnectorName} (${c.standard})`,
    }));

    const capabilityOptions = [
        { value: "CHARGING_PROFILE_CAPABLE" },
        { value: "CHARGING_PREFERENCES_CAPABLE" },
        { value: "CHIP_CARD_SUPPORT" },
        { value: "CREDIT_CARD_PAYABLE" },
        { value: "DEBIT_CARD_PAYABLE" },
        { value: "PED_TERMINAL" },
        { value: "REMOTE_START_STOP_CAPABLE" },
        { value: "RESERVABLE" },
        { value: "RFID_READER" },
        { value: "START_SESSION_CONNECTOR_REQUIRED" },
        { value: "TOKEN_GROUP_CAPABLE" },
        { value: "UNLOCK_CAPABLE" },
    ].map((option) => ({
        ...option,
        label: t(`evse.capability.${option.value}`),
    }));

    const parkingRestrictionOptions = [
        { value: "EV_ONLY" },
        { value: "PLUGGED" },
        { value: "DISABLED" },
        { value: "CUSTOMERS" },
        { value: "MOTORCYCLES" },
    ].map((option) => ({
        ...option,
        label: t(`evse.parkingRestriction.${option.value}`),
    }));

    const statusOptions = [
        { value: "AVAILABLE" },
        { value: "BLOCKED" },
        { value: "CHARGING" },
        { value: "INOPERATIVE" },
        { value: "OUTOFORDER" },
        { value: "PLANNED" },
        { value: "REMOVED" },
        { value: "RESERVED" },
        { value: "UNKNOWN" },
    ].map((option) => ({
        ...option,
        label: t(`evse.status.${option.value}`),
    }));

    const parkingSpaceOptions = (parkingSpaces || []).map((space) => ({
        value: space.parkingPublicId,
        label: space.physicalReference || space.parkingPublicId,
    }));

    const evsePositionOptions = [
        { value: "RIGHT", label: t("evse.position.RIGHT") },
        { value: "LEFT", label: t("evse.position.LEFT") },
        { value: "CENTER", label: t("evse.position.CENTER") },
    ];

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date)) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
        )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const parseInputToIso = (localStr) => {
        if (!localStr) return null;
        const d = new Date(localStr);
        return isNaN(d) ? null : d.toISOString();
    };

    // Handlers for list fields
    const addSchedule = () =>
        onChange("statusSchedules", [
            ...(connector.statusSchedules || []),
            { status: "", periodBeginUtc: "", periodEndUtc: "" },
        ]);

    const updateSchedule = (i, patch) => {
        const arr = [...(connector.statusSchedules || [])];
        arr[i] = { ...arr[i], ...patch };
        onChange("statusSchedules", arr);
    };

    const removeSchedule = (i) => {
        const arr = [...(connector.statusSchedules || [])];
        arr.splice(i, 1);
        onChange("statusSchedules", arr);
    };

    const addDirection = () =>
        onChange("directions", [...(connector.directions || []), { text: "", language: "" }]);

    const updateDirection = (i, patch) => {
        const arr = [...(connector.directions || [])];
        arr[i] = { ...arr[i], ...patch };
        onChange("directions", arr);
    };

    const removeDirection = (i) => {
        const arr = [...(connector.directions || [])];
        arr.splice(i, 1);
        onChange("directions", arr);
    };

    const addParking = () =>
        onChange("parkings", [
            ...(connector.parkings || []),
            { parkingPublicId: "", evsePosition: "" },
        ]);

    const updateParking = (i, patch) => {
        const arr = [...(connector.parkings || [])];
        arr[i] = { ...arr[i], ...patch };
        onChange("parkings", arr);
    };

    const removeParking = (i) => {
        const arr = [...(connector.parkings || [])];
        arr.splice(i, 1);
        onChange("parkings", arr);
    };

    return (
        <FormCard title={t("form.evseSettings")}>
            <FormGrid>
                {/* EvseCapabilities */}
                <SelectRow
                    label={t("form.evseCapabilities")}
                    value={connector.evseCapabilities}
                    options={capabilityOptions.map(p => ({
                        label: p.label,
                        value: p.value
                    }))}
                    onChange={(vals) => onChange("evseCapabilities", vals || [])}
                    isMulti
                />

                {/* Connectors — read only */}
                {connectorOptions.length > 0 && (
                    <DetailRow
                        label={t("tabs.connectors")}
                        tooltip={t("evse.tooltip.connectors")}
                        value={
                            <div className="flex flex-col">
                                {connectorOptions.map((c, idx) => (
                                    <span key={idx}>{c.label}</span>
                                ))}
                            </div>
                        }
                    />

                )}

                <InputRow
                    label={t("form.floorLevel")}
                    tooltip={t("evse.tooltip.floorLevel")}
                    value={connector.floorLevel}
                    onChange={(v) => onChange("floorLevel", v)}
                    invalid={isInvalid("floorLevel")}
                    maxLength={4}
                />

                <InputRow
                    label={t("common.latitude")}
                    tooltip={t("evse.tooltip.coordinates")}
                    value={connector.latitude}
                    onChange={(v) => onChange("latitude", v)}
                    invalid={isInvalid("latitude")}
                />

                <InputRow
                    label={t("common.longitude")}
                    tooltip={t("evse.tooltip.coordinates")}
                    value={connector.longitude}
                    onChange={(v) => onChange("longitude", v)}
                    invalid={isInvalid("longitude")}
                />

                <InputRow
                    label={t("form.physicalReference")}
                    tooltip={t("evse.tooltip.physicalReference")}
                    value={connector.physicalReference}
                    onChange={(v) => onChange("physicalReference", v)}
                    invalid={isInvalid("physicalReference")}
                    maxLength={16}
                />

                {/* Parking restrictions */}
                <SelectRow
                    label={t("form.parkingRestrictions")}
                    tooltip={t("evse.tooltip.parkingRestrictions")}
                    value={connector.parkingRestrictions}
                    options={parkingRestrictionOptions.map(p => ({
                        label: p.label,
                        value: p.value
                    }))}
                    onChange={(vals) => onChange("parkingRestrictions", vals || [])}
                    isMulti
                />

                <CreatableSelectRow
                    label={t("form.acceptedProviders")}
                    tooltip={t("evse.tooltip.acceptedServiceProviders")}
                    value={connector.acceptedServiceProviders}
                    options={[]}
                    onChange={(vals) => onChange("acceptedServiceProviders", vals || [])}
                    isMulti
                />
            </FormGrid>

            <div className="mt-10">
                <h3 className="text-lg font-semibold mb-3">{t("evse.parkings")}</h3>
                <button className="btn btn-primary mt-4" onClick={addParking}>
                    {t("common.addPlus")}
                </button>

                <div className="flex flex-col gap-4">
                    {(connector.parkings || []).map((parking, i) => (
                        <div
                            key={i}
                            className="py-4 border-b border-mute-200 grid grid-cols-1 md:grid-cols-3 gap-4"
                        >
                            <SelectRow
                                label={t("form.parking")}
                                tooltip={t("evse.tooltip.parking")}
                                value={parking.parkingPublicId || ""}
                                options={parkingSpaceOptions}
                                onChange={(v) => updateParking(i, { parkingPublicId: v })}
                                isMulti={false}
                            />

                            <SelectRow
                                label={t("form.evsePosition")}
                                value={parking.evsePosition || ""}
                                options={evsePositionOptions}
                                onChange={(v) => updateParking(i, { evsePosition: v })}
                                isMulti={false}
                            />

                            <button className="mt-6" onClick={() => removeParking(i)}>
                                <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
 
            <div className="mt-10">
                <h3 className="text-lg font-semibold mb-3">{t("evse.statusSchedules")}</h3>
                <button className="btn btn-primary mt-4" onClick={addSchedule}>
                    {t("common.addPlus")}
                </button>
                <div className="flex flex-col gap-4">
                    {(connector.statusSchedules || []).map((s, i) => (
                        <div key={i} className="py-4 border-b border-mute-200 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <SelectRow
                                label={t("common.status")}
                                selectedValues={statusOptions.find((o) => o.value === s.status) || null}
                                options={statusOptions}
                                onChange={(opt) => updateSchedule(i, { status: opt?.value || "" })}
                                isMulti={false}
                            />

                            <DateRow
                                label={t("form.startDateTime")}
                                value={formatDateForInput(s.periodBeginUtc)}
                                onChange={(v) => updateSchedule(i, { periodBeginUtc: parseInputToIso(v) })}
                            />

                            <DateRow
                                label={t("form.endDateTime")}
                                value={formatDateForInput(s.periodEndUtc)}
                                onChange={(v) => updateSchedule(i, { periodEndUtc: parseInputToIso(v) })}
                            />

                            <button className="mt-6" onClick={() => removeSchedule(i)}>
                                <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-10">
                <h3 className="text-lg font-semibold mb-3">{t("evse.directions")}</h3>
                <button className="btn btn-primary mt-4" onClick={addDirection}>
                    {t("common.addPlus")}
                </button>

                <div className="flex flex-col gap-4">
                    {(connector.directions || []).map((d, i) => (
                        <div
                            key={i}
                            className="py-4 border-b border-mute-200 grid grid-cols-1 md:grid-cols-3 gap-4"
                        >
                            <InputRow
                                label={t("common.text")}
                                tooltip={t("evse.tooltip.directions")}
                                value={d.text}
                                onChange={(v) => updateDirection(i, { text: v })}
                            />

                            <InputRow
                                label={t("common.languageCode")}
                                tooltip={t("evse.tooltip.directions")}
                                maxLength={2}
                                value={d.language}
                                onChange={(v) => updateDirection(i, { language: v })}
                            />

                            <button className="mt-6" onClick={() => removeDirection(i)}>
                                <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                            </button>
                        </div>
                    ))}
                </div>

            </div>

        </FormCard>
    );
};

export default EvseSettingsForm;
